<template>
  <div class="modal fade" id="payslipModal" tabindex="-1" aria-labelledby="payslipModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title text-white" id="payslipModalLabel">
            <i class="mdi mdi-file-document me-2 text-white"></i>Payslip
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body p-0">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading payslip data...</p>
          </div>

          <div v-else-if="error" class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline font-size-48 text-danger"></i>
            <h5 class="mt-2 text-danger">Failed to load payslip</h5>
            <p class="text-muted">{{ error }}</p>
          </div>

          <div v-else-if="!payslipData" class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline font-size-48 text-muted"></i>
            <p class="mt-2">No payslip data available</p>
          </div>

          <!-- Payslip Content -->
          <div v-else id="payslip-content" class="payslip-container">
            <!-- Company Header -->
            <div class="payslip-header">
              <div class="row align-items-center mb-4">
                <div class="col-md-3 text-center">
                  <div class="company-logo">
                    <img :src="county_logo()" alt="" height="70" class="auth-logo-light">
                  </div>
                </div>
                <div class="col-md-6 text-center">
                  <h3 class="company-name mb-1">{{ companyName }}</h3>
                  <p class="company-details mb-0">{{ companyAddress }}</p>
                  <p class="company-details mb-0">{{ companyContact }}</p>
                  <p class="company-details">{{ companyEmail }}</p>
                </div>
                <div class="col-md-3 text-end">
                  <div class="payslip-badge">
                    <h5 class="mb-0 text-white">PAYSLIP</h5>
                  </div>
                </div>
              </div>
              <div class="divider"></div>
            </div>

            <!-- Pay Period Information -->
            <div class="pay-period-section">
              <div class="row mb-3">
                <div class="col-md-6">
                  <table class="table table-sm table-borderless info-table">
                    <tbody>
                    <tr>
                      <td class="info-label">Pay Period:</td>
                      <td class="info-value">{{ payslipData.period_name }}</td>
                    </tr>
                    <tr>
                      <td class="info-label">Payment Date:</td>
                      <td class="info-value">{{ formatDate(payslipData.payment_date) }}</td>
                    </tr>
                    <tr>
                      <td class="info-label">Pay Frequency:</td>
                      <td class="info-value">Monthly</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div class="col-md-6">
                  <div class="employee-info-box">
                    <h6 class="section-title">EMPLOYEE INFORMATION</h6>
                    <table class="table table-sm table-borderless info-table">
                      <tbody>
                      <tr>
                        <td class="info-label">Name:</td>
                        <td class="info-value">{{ payslipData.employee_name }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">Employee No:</td>
                        <td class="info-value">{{ payslipData.empNo }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">Department:</td>
                        <td class="info-value">{{ payslipData.department }}</td>
                      </tr>
                      <tr v-if="payslipData.designation">
                        <td class="info-label">Designation:</td>
                        <td class="info-value">{{ payslipData.designation }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Earnings and Deductions -->
            <div class="earnings-deductions-section">
              <div class="row">
                <!-- Earnings Column -->
                <div class="col-md-6">
                  <div class="section-box earnings-box">
                    <h6 class="section-title earnings-title">
                      <i class="mdi mdi-cash-plus me-2"></i>EARNINGS
                    </h6>
                    <table class="table table-sm table-bordered">
                      <thead>
                      <tr>
                        <th>Description</th>
                        <th class="text-end">Amount (KES)</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr>
                        <td><strong>Basic Salary</strong></td>
                        <td class="text-end">{{ formatAmount(payslipData.basic_salary) }}</td>
                      </tr>

                      <!-- Allowances -->
                      <tr v-if="hasComponents('Allowance')" class="section-divider">
                        <td colspan="2"><strong>Allowances</strong></td>
                      </tr>
                      <tr v-for="allowance in components.Allowance" :key="allowance.id">
                        <td class="ps-3">{{ allowance.component_name }}</td>
                        <td class="text-end">{{ formatAmount(allowance.amount) }}</td>
                      </tr>

                      <tr class="total-row">
                        <td><strong>GROSS SALARY</strong></td>
                        <td class="text-end"><strong>{{ formatAmount(payslipData.gross_salary) }}</strong></td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Deductions Column -->
                <div class="col-md-6">
                  <div class="section-box deductions-box">
                    <h6 class="section-title deductions-title">
                      <i class="mdi mdi-cash-minus me-2"></i>DEDUCTIONS
                    </h6>
                    <table class="table table-sm table-bordered">
                      <thead>
                      <tr>
                        <th>Description</th>
                        <th class="text-end">Amount (KES)</th>
                      </tr>
                      </thead>
                      <tbody>
                      <!-- Statutory Deductions -->
                      <tr v-if="hasComponents('Deduction')" class="section-divider">
                        <td colspan="2"><strong>Statutory Deductions</strong></td>
                      </tr>
                      <tr v-for="deduction in components.Deduction" :key="deduction.id">
                        <td class="ps-3">{{ deduction.component_name }}</td>
                        <td class="text-end">{{ formatAmount(deduction.amount) }}</td>
                      </tr>

                      <!-- Tax -->
                      <tr v-if="hasComponents('Tax')" class="section-divider">
                        <td colspan="2"><strong>Tax</strong></td>
                      </tr>
                      <tr v-for="tax in components.Tax" :key="tax.id">
                        <td class="ps-3">{{ tax.component_name }}</td>
                        <td class="text-end">{{ formatAmount(tax.amount) }}</td>
                      </tr>

                      <!-- Relief -->
                      <tr v-if="hasComponents('Relief')" class="section-divider">
                        <td colspan="2"><strong>Tax Relief</strong></td>
                      </tr>
                      <tr v-for="relief in components.Relief" :key="relief.id" class="relief-row">
                        <td class="ps-3">{{ relief.component_name }}</td>
                        <td class="text-end">({{ formatAmount(Math.abs(relief.amount)) }})</td>
                      </tr>

                      <!-- Other Deductions -->
                      <tr v-if="hasComponents('Other')" class="section-divider">
                        <td colspan="2"><strong>Other Deductions</strong></td>
                      </tr>
                      <tr v-for="other in components.Other" :key="other.id">
                        <td class="ps-3">{{ other.component_name }}</td>
                        <td class="text-end">{{ formatAmount(other.amount) }}</td>
                      </tr>

                      <tr class="total-row">
                        <td><strong>TOTAL DEDUCTIONS</strong></td>
                        <td class="text-end"><strong>{{ formatAmount(payslipData.total_deductions) }}</strong></td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Net Pay Summary -->
            <div class="net-pay-section">
              <div class="net-pay-box">
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h4 class="mb-0 text-white">NET PAY</h4>
                    <p class="text-muted mb-0 small text-white">Amount payable to employee</p>
                  </div>
                  <div class="col-md-4 text-end">
                    <h2 class="net-amount mb-0">{{ formatCurrency(payslipData.net_salary) }}</h2>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Calculation Summary -->
            <div class="calculation-summary">
              <h6 class="section-title">PAYMENT CALCULATION</h6>
              <table class="table table-sm table-borderless calculation-table">
                <tbody>
                <tr>
                  <td>Gross Salary</td>
                  <td class="text-end">{{ formatCurrency(payslipData.gross_salary) }}</td>
                </tr>
                <tr>
                  <td>Less: Total Deductions</td>
                  <td class="text-end text-danger">({{ formatCurrency(payslipData.total_deductions) }})</td>
                </tr>
                <tr class="calculation-total">
                  <td><strong>Net Salary Payable</strong></td>
                  <td class="text-end"><strong>{{ formatCurrency(payslipData.net_salary) }}</strong></td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Payment Method -->
            <div class="payment-method-section" v-if="payslipData.payment_reference">
              <div class="row">
                <div class="col-md-6">
                  <p class="mb-1"><strong>Payment Method:</strong> Bank Transfer</p>
                  <p class="mb-0"><strong>Payment Reference:</strong> {{ payslipData.payment_reference }}</p>
                </div>
                <div class="col-md-6 text-end">
                  <p class="mb-1"><strong>Payment Status:</strong>
                    <span class="badge" :class="getPaymentStatusBadgeClass(payslipData.payment_status)">
                      {{ payslipData.payment_status }}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="payslip-footer">
              <div class="divider"></div>
              <div class="row">
                <div class="col-md-6">
                  <p class="footer-text mb-1"><small><strong>Generated on:</strong> {{ formatDateTime(new Date()) }}</small></p>
                  <p class="footer-text mb-0"><small><strong>Generated by:</strong> {{ payslipData.created_by || 'System' }}</small></p>
                </div>
                <div class="col-md-6 text-end">
                  <p class="footer-text mb-1"><small>This is a computer-generated document.</small></p>
                  <p class="footer-text mb-0"><small>No signature is required.</small></p>
                </div>
              </div>

              <div class="confidentiality-notice mt-3">
                <p class="mb-0 text-center">
                  <small><em>This payslip is confidential and intended solely for the addressee.
                    If you are not the intended recipient, please delete this document immediately.</em></small>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer no-print">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="mdi mdi-close me-1"></i> Close
          </button>
          <button type="button" class="btn btn-info" @click="downloadPDF">
            <i class="mdi mdi-download me-1"></i> Download PDF
          </button>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {authUrl, county_logo, execute} from "@/api";

export default {
  name: 'Payslip',
  props: {
    transactionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      payslipData: null,
      components: {
        Allowance: [],
        Deduction: [],
        Tax: [],
        Relief: [],
        Other: []
      },
      modal: null,
      companyName: 'Your Company Name',
      companyAddress: 'P.O. Box 12345-00100, Nairobi, Kenya',
      companyContact: 'Tel: +254 700 000 000',
      companyEmail: 'Email: info@company.com'
    };
  },
  mounted() {
    this.initModal();
  },
  methods: {
    initModal() {
      this.$nextTick(() => {
        this.modal = new bootstrap.Modal(document.getElementById('payslipModal'));
        document.getElementById('payslipModal').addEventListener('hidden.bs.modal', this.resetData);
      });
    },
    showModal(transactionId) {
      if (transactionId) {
        this.fetchPayslipData(transactionId);
      }

      if (this.modal) {
        this.modal.show();
      } else {
        this.initModal();
        setTimeout(() => {
          if (this.modal) this.modal.show();
        }, 100);
      }
    },
    county_logo() {
      return county_logo;
    },

    hideModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    resetData() {
      this.error = null;
      this.payslipData = null;
      this.components = {
        Allowance: [],
        Deduction: [],
        Tax: [],
        Relief: [],
        Other: []
      };
    },
    fetchPayslipData(transactionId) {
      this.loading = true;
      this.error = null;

      const data = new FormData();
      data.append("function", "getTransactionDetails");
      data.append("transactionId", transactionId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data && res.data.success) {
              this.payslipData = res.data.data.transaction;

              this.companyName = res.data.data.companyInfo.companyName
              this.companyAddress = res.data.data.companyInfo.companyAddress
              this.companyContact = res.data.data.companyInfo.companyContact
              this.companyEmail = res.data.data.companyInfo.companyEmail

              if (this.payslipData.components) {
                this.components = {
                  Allowance: this.payslipData.components.Allowance || [],
                  Deduction: this.payslipData.components.Deduction || [],
                  Tax: this.payslipData.components.Tax || [],
                  Relief: this.payslipData.components.Relief || [],
                  Other: this.payslipData.components.Other || []
                };
              }
            } else {
              this.error = res.data?.message || "Failed to load payslip data";
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            this.error = "An error occurred while loading payslip data. Please try again.";
          });
    },
    hasComponents(type) {
      return this.components[type] && this.components[type].length > 0;
    },
    formatAmount(value) {
      return new Intl.NumberFormat('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },
    formatDateTime(date) {
      if (!date) return 'N/A';
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(date).toLocaleString('en-US', options);
    },
    getPaymentStatusBadgeClass(status) {
      switch (status) {
        case 'Pending':
          return 'bg-warning';
        case 'Processing':
          return 'bg-info';
        case 'Paid':
          return 'bg-success';
        case 'Failed':
          return 'bg-danger';
        case 'Cancelled':
          return 'bg-dark';
        default:
          return 'bg-secondary';
      }
    },
    printPayslip() {
      window.print();
    },

    async downloadPDF() {
      this.loading = true;

      try {
        // Dynamically import required libraries
        const jsPDF = (await import('jspdf')).default;
        const autoTable = (await import('jspdf-autotable')).default;

        // Create a new PDF document
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // Add company logo
        try {
          const logoImg = new Image();
          logoImg.src = this.county_logo();

          // Wait for image to load
          await new Promise((resolve, reject) => {
            logoImg.onload = resolve;
            logoImg.onerror = reject;
          });

          // Add logo to PDF (left side)
          doc.addImage(logoImg, 'PNG', 15, 8, 25, 15);
        } catch (logoError) {
          console.warn('Logo could not be loaded:', logoError);
        }

        // Company Header (centered)
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text(this.companyName, pageWidth / 2, 15, { align: 'center' });

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(this.companyAddress, pageWidth / 2, 20, { align: 'center' });
        doc.text(this.companyContact + ' | ' + this.companyEmail, pageWidth / 2, 24, { align: 'center' });

        // PAYSLIP Badge (right side)
        doc.setFillColor(102, 126, 234);
        doc.roundedRect(pageWidth - 50, 10, 40, 12, 2, 2, 'F');
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('PAYSLIP', pageWidth - 30, 17, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Divider line
        doc.setDrawColor(102, 126, 234);
        doc.setLineWidth(0.5);
        doc.line(15, 28, pageWidth - 15, 28);

        // Pay Period and Employee Information - Side by Side
        let startY = 33;

        // Left side - Pay Period
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('Pay Period Information', 15, startY);

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text('Pay Period:', 15, startY + 5);
        doc.setTextColor(40, 40, 40);
        doc.text(this.payslipData.period_name, 45, startY + 5);

        doc.setTextColor(100, 100, 100);
        doc.text('Payment Date:', 15, startY + 10);
        doc.setTextColor(40, 40, 40);
        doc.text(this.formatDate(this.payslipData.payment_date), 45, startY + 10);

        doc.setTextColor(100, 100, 100);
        doc.text('Pay Frequency:', 15, startY + 15);
        doc.setTextColor(40, 40, 40);
        doc.text('Monthly', 45, startY + 15);

        // Right side - Employee Information (with background)
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(pageWidth / 2 + 5, startY - 3, pageWidth / 2 - 20, 22, 2, 2, 'F');
        doc.setDrawColor(102, 126, 234);
        doc.setLineWidth(1.5);
        doc.line(pageWidth / 2 + 5, startY - 3, pageWidth / 2 + 5, startY + 19);

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('EMPLOYEE INFORMATION', pageWidth / 2 + 10, startY);

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text('Name:', pageWidth / 2 + 10, startY + 5);
        doc.setTextColor(40, 40, 40);
        doc.text(this.payslipData.employee_name, pageWidth / 2 + 30, startY + 5);

        doc.setTextColor(100, 100, 100);
        doc.text('Employee No:', pageWidth / 2 + 10, startY + 10);
        doc.setTextColor(40, 40, 40);
        doc.text(String(this.payslipData.empNo), pageWidth / 2 + 30, startY + 10);

        doc.setTextColor(100, 100, 100);
        doc.text('Department:', pageWidth / 2 + 10, startY + 15);
        doc.setTextColor(40, 40, 40);
        doc.text(this.payslipData.department, pageWidth / 2 + 30, startY + 15);

        startY += 26;

        // Earnings and Deductions Tables - Side by Side
        // Earnings Section
        const earningsData = [
          ['Basic Salary', this.formatAmount(this.payslipData.basic_salary)]
        ];

        if (this.hasComponents('Allowance')) {
          this.components.Allowance.forEach(allowance => {
            earningsData.push([allowance.component_name, this.formatAmount(allowance.amount)]);
          });
        }

        earningsData.push(['GROSS SALARY', this.formatAmount(this.payslipData.gross_salary)]);

        autoTable(doc, {
          startY: startY,
          head: [['EARNINGS', 'Amount (KES)']],
          body: earningsData,
          theme: 'striped',
          headStyles: {
            fillColor: [40, 167, 69],
            textColor: 255,
            fontSize: 9,
            fontStyle: 'bold',
            halign: 'left'
          },
          columnStyles: {
            0: { cellWidth: 55, fontSize: 8 },
            1: { cellWidth: 30, halign: 'right', fontSize: 8 }
          },
          styles: {
            fontSize: 8,
            cellPadding: 2,
            lineColor: [220, 220, 220],
            lineWidth: 0.1
          },
          margin: { left: 15, right: pageWidth / 2 + 5 },
          didParseCell: (data) => {
            if (data.section === 'body' && data.row.index === earningsData.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [220, 237, 200];
              data.cell.styles.textColor = [40, 40, 40];
            }
          }
        });

        // Deductions Section
        const deductionsData = [];

        if (this.hasComponents('Deduction')) {
          this.components.Deduction.forEach(deduction => {
            deductionsData.push([deduction.component_name, this.formatAmount(deduction.amount)]);
          });
        }

        if (this.hasComponents('Tax')) {
          this.components.Tax.forEach(tax => {
            deductionsData.push([tax.component_name, this.formatAmount(tax.amount)]);
          });
        }

        if (this.hasComponents('Relief')) {
          this.components.Relief.forEach(relief => {
            deductionsData.push([relief.component_name + ' (Relief)', '(' + this.formatAmount(Math.abs(relief.amount)) + ')']);
          });
        }

        if (this.hasComponents('Other')) {
          this.components.Other.forEach(other => {
            deductionsData.push([other.component_name, this.formatAmount(other.amount)]);
          });
        }

        deductionsData.push(['TOTAL DEDUCTIONS', this.formatAmount(this.payslipData.total_deductions)]);

        autoTable(doc, {
          startY: startY,
          head: [['DEDUCTIONS', 'Amount (KES)']],
          body: deductionsData,
          theme: 'striped',
          headStyles: {
            fillColor: [220, 53, 69],
            textColor: 255,
            fontSize: 9,
            fontStyle: 'bold',
            halign: 'left'
          },
          columnStyles: {
            0: { cellWidth: 55, fontSize: 8 },
            1: { cellWidth: 30, halign: 'right', fontSize: 8 }
          },
          styles: {
            fontSize: 8,
            cellPadding: 2,
            lineColor: [220, 220, 220],
            lineWidth: 0.1
          },
          margin: { left: pageWidth / 2 + 10, right: 15 },
          didParseCell: (data) => {
            if (data.section === 'body' && data.row.index === deductionsData.length - 1) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [248, 215, 218];
              data.cell.styles.textColor = [40, 40, 40];
            }
          }
        });

        // Get current Y position after tables
        let currentY = doc.lastAutoTable.finalY + 8;

        // Net Pay Section - Full Width
        doc.setFillColor(102, 126, 234);
        doc.roundedRect(15, currentY, pageWidth - 30, 16, 2, 2, 'F');

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('NET PAY', 20, currentY + 7);

        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.text('Amount payable to employee', 20, currentY + 11);

        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text(this.formatCurrency(this.payslipData.net_salary), pageWidth - 20, currentY + 10, { align: 'right' });
        doc.setFont(undefined, 'normal');

        currentY += 20;

        // Payment Calculation Summary
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(40, 40, 40);
        doc.text('PAYMENT CALCULATION', 15, currentY);
        doc.setFont(undefined, 'normal');

        const calculationData = [
          ['Gross Salary', this.formatCurrency(this.payslipData.gross_salary)],
          ['Less: Total Deductions', '(' + this.formatCurrency(this.payslipData.total_deductions) + ')'],
          ['Net Salary Payable', this.formatCurrency(this.payslipData.net_salary)]
        ];

        autoTable(doc, {
          startY: currentY + 3,
          head: [],
          body: calculationData,
          theme: 'plain',
          columnStyles: {
            0: { cellWidth: 'auto', fontStyle: 'bold', fontSize: 8 },
            1: { cellWidth: 50, halign: 'right', fontStyle: 'bold', fontSize: 8 }
          },
          styles: { cellPadding: 1.5 },
          didParseCell: (data) => {
            if (data.row.index === 1) {
              data.cell.styles.textColor = [220, 53, 69];
            }
            if (data.row.index === 2) {
              data.cell.styles.fontSize = 9;
              data.cell.styles.fillColor = [240, 240, 240];
            }
          }
        });

        currentY = doc.lastAutoTable.finalY + 6;

        // Payment Method (if available)
        if (this.payslipData.payment_reference) {
          doc.setFillColor(255, 243, 205);
          doc.roundedRect(15, currentY, pageWidth - 30, 12, 2, 2, 'F');
          doc.setDrawColor(255, 193, 7);
          doc.setLineWidth(1);
          doc.line(15, currentY, 15, currentY + 12);

          doc.setFontSize(7);
          doc.setTextColor(40, 40, 40);
          doc.text('Payment Method: Bank Transfer | Ref: ' + this.payslipData.payment_reference, 18, currentY + 5);
          doc.text('Status: ' + this.payslipData.payment_status, pageWidth - 18, currentY + 5, { align: 'right' });

          currentY += 15;
        }

        // Footer Section
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        doc.line(15, currentY, pageWidth - 15, currentY);
        currentY += 4;

        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text('Generated: ' + this.formatDateTime(new Date()), 15, currentY);
        doc.text('By: ' + (this.payslipData.created_by || 'System'), 15, currentY + 3);

        doc.text('Computer-generated document', pageWidth - 15, currentY, { align: 'right' });
        doc.text('No signature required', pageWidth - 15, currentY + 3, { align: 'right' });

        currentY += 7;

        // Confidentiality Notice
        doc.setFillColor(248, 249, 250);
        doc.roundedRect(15, currentY, pageWidth - 30, 8, 1, 1, 'F');
        doc.setFontSize(6);
        doc.setTextColor(108, 117, 125);
        const confidentialityText = 'CONFIDENTIAL: This payslip is intended solely for the addressee. Unauthorized access or distribution is prohibited.';
        doc.text(confidentialityText, pageWidth / 2, currentY + 5, { align: 'center', maxWidth: pageWidth - 35 });

        // Save the PDF
        const fileName = `Payslip_${this.payslipData.employee_id}_${this.payslipData.period_name.replace(/\s+/g, '_')}.pdf`;
        doc.save(fileName);

        alert('PDF payslip generated successfully');
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF: ' + error.message);
      } finally {
        this.loading = false;
      }
    }

  }
};
</script>

<style scoped>
/* Print Styles */
@media print {
  body * {
    visibility: hidden;
  }

  #payslip-content,
  #payslip-content * {
    visibility: visible;
  }

  #payslip-content {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 0 !important;
    margin: 0 !important;
  }

  .no-print {
    display: none !important;
  }

  .modal-header,
  .modal-footer {
    display: none !important;
  }

  .payslip-container {
    box-shadow: none !important;
    padding: 15px !important;
    max-width: 100% !important;
    page-break-inside: avoid;
  }

  /* Reduce spacing for print */
  .payslip-header {
    margin-bottom: 15px !important;
  }

  .pay-period-section {
    margin-bottom: 15px !important;
  }

  .earnings-deductions-section {
    margin-bottom: 15px !important;
  }

  .net-pay-section {
    margin-bottom: 15px !important;
  }

  .calculation-summary {
    margin-bottom: 15px !important;
    padding: 15px !important;
  }

  .payment-method-section {
    margin-bottom: 15px !important;
    padding: 10px !important;
  }

  .payslip-footer {
    margin-top: 15px !important;
    padding-top: 10px !important;
  }

  .section-box {
    padding: 12px !important;
  }

  .net-pay-box {
    padding: 15px !important;
  }

  /* Reduce table spacing */
  .table {
    margin-bottom: 0 !important;
  }

  .table td, .table th {
    padding: 4px 8px !important;
    font-size: 11px !important;
  }

  .info-table td {
    padding: 3px 0 !important;
    font-size: 11px !important;
  }

  /* Reduce title sizes */
  h3, h4, h5, h6 {
    margin-bottom: 8px !important;
  }

  .company-name {
    font-size: 18px !important;
    margin-bottom: 4px !important;
  }

  .company-details {
    font-size: 10px !important;
    margin-bottom: 2px !important;
  }

  .section-title {
    font-size: 11px !important;
    margin-bottom: 8px !important;
  }

  .net-amount {
    font-size: 28px !important;
  }

  /* Ensure no page breaks inside critical sections */
  .employee-info-box,
  .section-box,
  .net-pay-box,
  .calculation-summary {
    page-break-inside: avoid;
  }

  .divider {
    height: 2px !important;
    margin: 10px 0 !important;
  }

  .confidentiality-notice {
    padding: 8px !important;
    margin-top: 10px !important;
  }

  @page {
    size: A4 portrait;
    margin: 12mm;
  }
}

/* Payslip Container Styles */
.payslip-container {
  background: white;
  padding: 30px;
  font-family: 'Arial', sans-serif;
  color: #333;
  max-width: 800px;
  margin: 0 auto;
  font-size: 13px;
}

/* Header Section */
.payslip-header {
  margin-bottom: 20px;
}

.company-logo {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
  display: inline-block;
}

.company-name {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 5px;
  font-size: 24px;
}

.company-details {
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.4;
}

.payslip-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px 25px;
  border-radius: 8px;
  display: inline-block;
}

/* Divider */
.divider {
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  margin: 20px 0;
}

/* Pay Period Section */
.pay-period-section {
  margin-bottom: 20px;
}

.employee-info-box {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.section-title {
  color: #2c3e50;
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
}

.info-table {
  margin-bottom: 0;
}

.info-label {
  color: #7f8c8d;
  font-weight: 500;
  width: 40%;
  padding: 4px 0;
  font-size: 12px;
}

.info-value {
  color: #2c3e50;
  font-weight: 600;
  padding: 4px 0;
  font-size: 12px;
}

/* Earnings and Deductions Section */
.earnings-deductions-section {
  margin-bottom: 20px;
}

.section-box {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  height: 100%;
}

.earnings-box {
  border-left: 4px solid #28a745;
}

.deductions-box {
  border-left: 4px solid #dc3545;
}

.earnings-title {
  color: #28a745;
}

.deductions-title {
  color: #dc3545;
}

.section-box table {
  margin-bottom: 0;
  font-size: 12px;
}

.section-box thead th {
  background: #f8f9fa;
  color: #495057;
  font-weight: 600;
  padding: 8px;
  border-bottom: 2px solid #dee2e6;
}

.section-box tbody td {
  padding: 6px 8px;
  vertical-align: middle;
}

.section-divider {
  background: #f8f9fa;
  font-weight: 600;
}

.relief-row td {
  color: #28a745;
  font-style: italic;
}

.total-row {
  background: #e9ecef;
  font-weight: 700;
}

.total-row td {
  padding: 12px 10px !important;
  font-size: 14px;
}

/* Net Pay Section */
.net-pay-section {
  margin-bottom: 20px;
}

.net-pay-box {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.net-amount {
  font-size: 32px;
  font-weight: 700;
  color: white;
}

/* Calculation Summary */
.calculation-summary {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.calculation-table {
  margin-top: 15px;
  margin-bottom: 0;
}

.calculation-table td {
  padding: 8px 0;
  font-size: 14px;
}

.calculation-total {
  border-top: 2px solid #dee2e6;
  padding-top: 10px;
}

.calculation-total td {
  padding-top: 15px !important;
  font-size: 16px;
  color: #2c3e50;
}

/* Payment Method Section */
.payment-method-section {
  padding: 12px;
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  border-radius: 5px;
  margin-bottom: 20px;
}

/* Footer */
.payslip-footer {
  margin-top: 20px;
  padding-top: 15px;
}

.footer-text {
  color: #6c757d;
  font-size: 12px;
}

.confidentiality-notice {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 5px;
  border: 1px dashed #dee2e6;
}

.confidentiality-notice p {
  color: #6c757d;
  font-size: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .payslip-container {
    padding: 20px;
  }

  .net-amount {
    font-size: 28px;
  }

  .company-name {
    font-size: 20px;
  }
}

/* Font size adjustments */
.font-size-48 {
  font-size: 48px !important;
}
</style>